import React from 'react';
import Icon from '../../../components/AppIcon';

const relTime = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const StatBox = ({ value, label, color }) => (
  <div className="flex flex-col items-center gap-0.5 py-2">
    <span className={`text-xl font-bold leading-none ${color}`}>{value ?? 0}</span>
    <span className="text-[10px] text-[#94A3B8] text-center leading-tight">{label}</span>
  </div>
);

const LiveQueuePanel = ({ stats = {}, pendingJobs = [], oldestJobAge = 0, avgProcessingTime = 0, loading }) => {
  const pending = stats.pending || 0;
  const processing = stats.processing || 0;
  const generating = stats.generating || 0;
  const completed = stats.completed || 0;
  const failed = stats.failed || 0;
  const activeTotal = pending + processing + generating;
  const isEmpty = activeTotal === 0 && completed === 0 && failed === 0;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm animate-pulse">
        <div className="p-5 border-b border-[#F1F5F9]">
          <div className="h-4 bg-slate-100 rounded w-1/3 mb-1.5" />
          <div className="h-3 bg-slate-100 rounded w-1/2" />
        </div>
        <div className="grid grid-cols-5 divide-x divide-[#F1F5F9] border-b border-[#F1F5F9]">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex flex-col items-center gap-1 py-3 px-2">
              <div className="h-5 w-8 bg-slate-100 rounded" />
              <div className="h-2.5 w-12 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
        <div className="p-4 space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-10 bg-slate-100 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Automation Queue</h3>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">Live job statuses</p>
        </div>
        {activeTotal > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            {activeTotal} active
          </span>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-5 divide-x divide-[#F1F5F9] border-b border-[#F1F5F9]">
        <StatBox value={pending} label="Pending" color="text-amber-500" />
        <StatBox value={processing + generating} label="Running" color="text-blue-500" />
        <StatBox value={completed} label="Done" color="text-emerald-500" />
        <StatBox value={failed} label="Failed" color="text-red-500" />
        <StatBox value={avgProcessingTime > 0 ? `${avgProcessingTime}s` : '—'} label="Avg Time" color="text-[#64748B]" />
      </div>

      {/* Meta bar */}
      {(oldestJobAge > 0 || pending > 0) && (
        <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#F1F5F9] flex items-center justify-between text-[11px] text-[#94A3B8]">
          <span className="flex items-center gap-1">
            <Icon name="Clock" size={11} />
            Oldest: {oldestJobAge > 0 ? `${oldestJobAge}m ago` : 'just now'}
          </span>
          <span>{(stats.total || 0)} total today</span>
        </div>
      )}

      {/* Job list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1.5" style={{ maxHeight: 220 }}>
        {isEmpty || pendingJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-[#94A3B8] gap-2">
            <Icon name="CheckCircle" size={22} className="text-emerald-400" />
            <p className="text-sm font-medium text-emerald-600">Queue is clear</p>
          </div>
        ) : (
          pendingJobs.map(job => (
            <div key={job.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#F8FAFC] transition-colors group">
              <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Icon name="Clock" size={11} className="text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#0F172A] truncate">{job.title || 'Untitled'}</p>
              </div>
              <span className="text-[10px] text-[#94A3B8] flex-shrink-0">{relTime(job.created_at)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LiveQueuePanel;
